import _isEmpty from 'lodash/isEmpty';
import _isEqual from 'lodash/isEqual';
import _maxBy from 'lodash/maxBy';
import _get from 'lodash/get';
import classnames from 'classnames/bind';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import I18n from 'i18n-js';
import {
  getCompany,
  getCompanyID,
  getUserSelectedCityCountryCode,
  getUserCountryCode,
} from 'helper/user';
import { connect } from 'react-redux';
import { Drawer, Button } from 'grab-ui';
import debounce from 'common/utils/debounce';
import { toggleLocationDrawerAction } from 'actions/app-actions';
import { trackClick, event, cat, custom } from 'common/utils/instrumentation';
import { userInfoSelector } from 'selectors/app-selector';
import { isProduction } from 'common/utils/environment-util';
import reorder from 'helper/reorder';
import Map from 'components/booking/map/map';
import { getFormattedAmount } from 'helper/quote';
import {
  scribeTrackEvent,
  getCommonFacet,
  SCRIBE_STATE_NAMES,
  SCRIBE_POI_SEARCH_EVENTS,
  SCRIBE_EVENT_PARAMS,
} from 'applications/scribe/instrumentations/track';

import AddressField from '../address-field/address-field';
import css from './location-drawer.less';

const cx = classnames.bind(css);

class LocationDrawer extends Component {
  constructor(props) {
    super(props);
    this.company = getCompany(props.userInfo) || {};
    this.countryCode = getUserCountryCode(props.userInfo);
    this.selectedAddresses = [];
    const drawerSize = this.calculateDrawerSizeAndDirection();

    this.state = {
      ...drawerSize,
      mapLatLng: {},
      addressFields: this.setUpAddressFields(),
    };
    this.showQuote = !isProduction();
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentDidUpdate(prevProps) {
    const prevLocations = _get(prevProps, 'locationDrawer.locations');
    const currentLocations = _get(this.props, 'locationDrawer.locations');
    if (!_isEqual(prevLocations, currentLocations)) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        addressFields: this.setUpAddressFields(),
      });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  onSelectAddress = (id, address) => {
    const { userInfo, locationDrawer: { isSender } } = this.props;
    const companyID = getCompanyID(userInfo);
    trackClick(event.POI_CLICKS, cat.GE_POI_SEARCH, 'POI_SELECT', {
      [custom.COMPANY_ID]: companyID,
      STEP: isSender ? 'PICKUP' : 'DROPOFF',
      POI_ID: address.id,
    });
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_POI_SEARCH_WEB,
      eventName: SCRIBE_POI_SEARCH_EVENTS.POI_SELECT,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.STEP_TYPE]: isSender ? 'PICKUP' : 'DROPOFF',
        [isSender
          ? SCRIBE_EVENT_PARAMS.PICKUP_POI_ID
          : SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID]: address.id,
      },
    });

    this.setState({
      mapLatLng: address.latlng,
    });
    this.selectedAddresses[id] = address;
    this.updateQuote();
  };

  onClose = () => {
    this.setState({
      addressFields: [],
    });
    this.selectedAddresses = [];
    const { toggleLocationDrawer } = this.props;
    toggleLocationDrawer(null);
  };

  onDelete = index => {
    const { addressFields } = this.state;
    addressFields.splice(index, 1);
    this.selectedAddresses.splice(index, 1);
    this.updateQuote();
    this.setState({ addressFields });
  };

  onDragEnd = result => {
    const {
      source: { index: sourceIndex },
      destination: { index: destIndex },
    } = result;
    const addressFields = reorder(
      this.state.addressFields,
      sourceIndex,
      destIndex,
    );
    this.selectedAddresses = reorder(
      this.selectedAddresses,
      sourceIndex,
      destIndex,
    );
    this.updateQuote();
    this.setState({
      addressFields,
    });
  };

  setUpAddressFields = () => {
    const { locationDrawer: { locations } } = this.props;
    if (!locations || _isEmpty(locations[0])) {
      return [this.createAddressFieldProps(0)];
    }

    const validLocations = locations.filter(l => !l.deleted);
    return validLocations.map((loc, index) =>
      this.createAddressFieldProps(index, loc),
    );
  };

  createAddressFieldProps = (key, loc = '') => {
    const { userInfo, locationDrawer: { isSender } } = this.props;
    return {
      key: String(key),
      afProps: {
        city: userInfo.selectedCity,
        company: this.company,
        isPickupAddress: true,
        onSelectAddress: this.onSelectAddress,
        placeholder: isSender
          ? I18n.t('v2.locationDrawer.placeholder.addPickupLocation')
          : I18n.t('v2.locationDrawer.placeholder.addDropOffLocation'),
        initialValue: !_isEmpty(loc)
          ? `${loc.latlng.latitude},${loc.latlng.longitude}`
          : '',
        onDelete: this.onDelete,
      },
    };
  };

  handleResize = debounce(
    () => {
      this.setState(this.calculateDrawerSizeAndDirection());
    },
    150,
    false,
  );

  calculateDrawerSizeAndDirection = () => {
    if (window.innerWidth < 932) {
      return { width: '100%', placement: 'bottom' };
    }
    return { width: 495, placement: 'right' };
  };

  addStop = () => {
    const { userInfo } = this.props;
    const companyID = getCompanyID(userInfo);
    trackClick(event.POI_CLICKS, cat.GE_POI_SEARCH, 'ADD_STOP', {
      [custom.COMPANY_ID]: companyID,
    });
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_POI_SEARCH_WEB,
      eventName: SCRIBE_POI_SEARCH_EVENTS.ADD_STOP,
      facets: getCommonFacet(userInfo),
    });

    const fields = this.state.addressFields;
    const maxKey = _maxBy(fields, 'key');
    fields.push(this.createAddressFieldProps(_get(maxKey, 'key', 0) + 1));
    this.setState({
      addressFields: fields,
    });
  };

  updateQuote = () => {
    if (!this.showQuote) return;
    const { onUpdate } = this.props;
    onUpdate(this.selectedAddresses);
  };

  confirmClicked = () => {
    const {
      onUpdate,
      toggleLocationDrawer,
      userInfo,
      locationDrawer: { isSender },
    } = this.props;
    const companyID = getCompanyID(userInfo);
    const poiIDs = this.selectedAddresses.map(a => a.id);
    trackClick(event.POI_CLICKS, cat.GE_POI_SEARCH, 'CONFIRM', {
      [custom.COMPANY_ID]: companyID,
      STEP: isSender ? 'PICKUP' : 'DROPOFF',
      POI_ID: poiIDs,
    });
    scribeTrackEvent({
      stateName: SCRIBE_STATE_NAMES.EXPRESS_POI_SEARCH_WEB,
      eventName: SCRIBE_POI_SEARCH_EVENTS.CONFIRM,
      facets: {
        ...getCommonFacet(userInfo),
        [SCRIBE_EVENT_PARAMS.STEP_TYPE]: isSender ? 'PICKUP' : 'DROPOFF',
        [isSender
          ? SCRIBE_EVENT_PARAMS.PICKUP_POI_ID
          : SCRIBE_EVENT_PARAMS.DROPOFF_POI_ID]: poiIDs,
      },
    });

    onUpdate(this.selectedAddresses);
    this.setState({
      addressFields: [],
    });
    this.selectedAddresses = [];
    toggleLocationDrawer(null);
  };

  renderAddressFields = () => {
    const { locationDrawer, allowMultipleAddresses, maxMSDStops } = this.props;
    const { addressFields } = this.state;
    const { isSender } = locationDrawer;
    if (!addressFields || addressFields.length === 0) return null;
    return (
      <div>
        <div className={css.addressFields}>
          {addressFields.map((af, index, items) => {
            const showNumbers = items.length > 1;
            return (
              <div key={af.key} className={cx('afRow')}>
                <div className={cx('afContainer', 'withoutDragHandle')}>
                  <AddressField
                    {...af.afProps}
                    enableDelete={items.length > 1}
                    index={index}
                    showNumbers={showNumbers}
                    isSender={isSender}
                  />
                </div>
              </div>
            );
          })}
          {allowMultipleAddresses &&
            addressFields.length < maxMSDStops && (
              <div className={css.addStop}>
                <Button onClick={this.addStop}>
                  + {I18n.t('v2.homepage.manualOrder.button.addStop')}
                </Button>
              </div>
            )}
        </div>
      </div>
    );
  };

  renderQuote = () => {
    const { order, userInfo } = this.props;
    const quoteForRender = _get(order, 'quote.forRender');
    if (!this.showQuote || !quoteForRender) return null;
    return (
      <div className={css.quoteSection}>
        <span>{I18n.t('v2.locationDrawer.quoteTotal')}</span>
        <span className={css.quotePrice}>
          {getFormattedAmount(
            quoteForRender,
            getUserSelectedCityCountryCode(userInfo),
          )}
        </span>
      </div>
    );
  };

  render() {
    const { locationDrawer } = this.props;
    const { width, placement, mapLatLng } = this.state;
    const { opening, isSender } = locationDrawer;

    return (
      <Drawer
        width={width}
        placement={placement}
        visible={opening}
        className={css.locationDrawer}
        closable
        height="100%"
        onClose={this.onClose}
      >
        <div className={css.body}>
          <div className={css.map}>
            <Map
              id={'map'}
              countryCode={this.countryCode}
              latlng={mapLatLng}
              isSender={isSender}
            />
          </div>
          {this.renderAddressFields()}
          <div className={css.footer}>
            {this.renderQuote()}
            <Button type="primary" onClick={this.confirmClicked}>
              {I18n.t('v2.commonButton.confirm')}
            </Button>
          </div>
        </div>
      </Drawer>
    );
  }
}

LocationDrawer.propTypes = {
  userInfo: PropTypes.shape({}).isRequired,
  locationDrawer: PropTypes.shape({
    opening: PropTypes.bool.isRequired,
    locations: PropTypes.arrayOf(PropTypes.shape({})),
    allowDrag: PropTypes.bool.isRequired,
  }).isRequired,
  onUpdate: PropTypes.func.isRequired,
  toggleLocationDrawer: PropTypes.func.isRequired,
  allowMultipleAddresses: PropTypes.bool.isRequired,
  order: PropTypes.shape({}),
  maxMSDStops: PropTypes.number,
};

LocationDrawer.defaultProps = {
  order: {},
  maxMSDStops: 1,
};

const mapStateToProps = state => ({
  userInfo: userInfoSelector(state),
  locationDrawer: state.app.locationDrawer,
});

export const mapDispatchToProps = dispatch => ({
  toggleLocationDrawer: () => dispatch(toggleLocationDrawerAction()),
});

export default connect(mapStateToProps, mapDispatchToProps)(LocationDrawer);
